import React, { useState, useEffect } from "react";
import { fetchUserHours } from "../api";

export default function TotalHours() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userData, setUserData] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // ✅ Automatically fetch this week's hours on load
  useEffect(() => {
    const today = new Date();
    const firstDayOfWeek = new Date(today);
    firstDayOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
    const lastDayOfWeek = new Date(firstDayOfWeek);
    lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6); // Sunday

    const start = firstDayOfWeek.toISOString().split("T")[0];
    const end = lastDayOfWeek.toISOString().split("T")[0];

    setStartDate(start);
    setEndDate(end);

    fetchHours(start, end);
  }, []);

  // ✅ Function to fetch data
  const fetchHours = async (start, end) => {
    setLoading(true);
    setMessage("");

    try {
      const params = { startDate: start, endDate: end };
      const res = await fetchUserHours(params);
      const employees = res.data?.employees || [];

      // Group by employee + location
      const aggregated = Object.values(
        employees.reduce((acc, emp) => {
          const key = `${emp.employeeName}-${emp.locationName}`;
          if (!acc[key]) acc[key] = { ...emp };
          else acc[key].workedHours += emp.workedHours;
          return acc;
        }, {})
      );

     setUserData(aggregated);

// ✅ Calculate total hours manually from the employee list
const total = aggregated.reduce((sum, emp) => sum + (emp.workedHours || 0), 0);
setTotalHours(total);

    } catch (err) {
      console.error("Error fetching total hours:", err);
      setMessage("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Apply click
  const handleApply = () => {
    if (!startDate || !endDate) {
      setMessage("Please select both start and end dates.");
      return;
    }
    fetchHours(startDate, endDate);
  };

  // ✅ CSV download
  const handleDownloadCSV = () => {
    if (!userData || userData.length === 0) {
      setMessage("No data available to download.");
      return;
    }

    const headers = ["Employee Name", "Location", "Worked Hours"];
    const rows = userData.map((u) => [
      u.employeeName || "-",
      u.locationName || "-",
      u.workedHours?.toFixed(2) || "0",
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `working-hours-${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 bg-white rounded-2xl shadow-md text-center">
      {/* Header + Controls */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <h2 className="text-lg font-semibold text-gray-800">
          Total Hours 
        </h2>

        {message && <p className="text-red-500 text-sm w-full">{message}</p>}

        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Inputs */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
          />

          {/* Apply */}
          <button
            onClick={handleApply}
            className=" bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded-lg"
          >
            Apply
          </button>

          {/* Download */}
          <button
            onClick={handleDownloadCSV}
            className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded-lg"
          >
            Download
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <p className="text-gray-500 py-4">Loading...</p>
      ) : (
        <div className="py-2">
          <h3 className="text-2xl font-bold text-blue-600">
            {totalHours.toFixed(2)}
          </h3>
          {startDate && endDate && (
            <p className="text-gray-500 text-sm mt-1">
              Total Hours from <strong>{startDate}</strong> to{" "}
              <strong>{endDate}</strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
